// Simple localStorage-based onboarding manager
const ONBOARDING_KEY = 'synkro_onboarding_completed';

const OnboardingManager = {
  isCompleted: (userId?: string) => {
    if (!userId) return false;
    return localStorage.getItem(`${ONBOARDING_KEY}_${userId}`) === 'true';
  },
  markCompleted: (userId?: string) => {
    if (!userId) return;
    localStorage.setItem(`${ONBOARDING_KEY}_${userId}`, 'true');
  },
  reset: (userId?: string) => {
    if (!userId) return;
    localStorage.removeItem(`${ONBOARDING_KEY}_${userId}`);
  }
};

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useNavigation } from './NavigationContext';
import { useLocation } from 'react-router-dom';
import { getManagerTourSteps, getConsultantTourSteps, getClientTourSteps, getBusinessTourSteps } from '@/components/onboarding/TourSteps';

interface OnboardingContextType {
  currentStep: number;
  totalSteps: number;
  isOnboardingActive: boolean;
  startOnboarding: () => void;
  skipOnboarding: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  userRole: string | null;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const { user, profile } = useAuth();
  const { currentMode } = useNavigation();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [totalSteps, setTotalSteps] = useState(0);

  // Set user role and total steps when profile or current mode changes
  useEffect(() => {
    if (profile?.role) {
      setUserRole(profile.role);
      // For admin users, use currentMode; for others, use profile role
      const effectiveRole = profile.role === 'admin' ? currentMode : profile.role;
      const steps = getTotalStepsForRole(effectiveRole);
      setTotalSteps(steps);
    } else {
      setUserRole(null);
      setTotalSteps(0);
    }
  }, [profile?.role, currentMode]);

  // Auto-start onboarding for new users (only on appropriate pages)
  useEffect(() => {
    if (user?.id && profile?.role && totalSteps > 0) {
      const isCompleted = OnboardingManager.isCompleted(user.id);

      // Only start onboarding on dashboard pages, not on auth/landing/role selection
      const isOnAppropriatePage = location.pathname.includes('/dashboard') ||
        location.pathname.includes('/consultant/dashboard') ||
        location.pathname.includes('/manager/dashboard') ||
        location.pathname.includes('/clientportal/dashboard') ||
        location.pathname.includes('/business/');

      if (!isCompleted && isOnAppropriatePage) {
        setCurrentStep(0);
        setIsOnboardingActive(true);
      } else if (isCompleted) {
        // If onboarding is completed, set currentStep to totalSteps to show completion status
        setCurrentStep(totalSteps);
        setIsOnboardingActive(false);
      }
    }
  }, [user?.id, profile?.role, totalSteps, location.pathname]);

  const getTotalStepsForRole = (effectiveRole: string | null): number => {
    switch (effectiveRole) {
      case 'manager':
        return getManagerTourSteps().length;
      case 'consultant':
        return getConsultantTourSteps().length;
      case 'client':
        return getClientTourSteps().length;
      case 'business':
        return getBusinessTourSteps().length;
      default:
        return 0;
    }
  };

  const startOnboarding = () => {
    setCurrentStep(0);
    setIsOnboardingActive(true);
  };

  const skipOnboarding = () => {
    setIsOnboardingActive(false);
    setCurrentStep(totalSteps);
    OnboardingManager.markCompleted(user?.id);
  };

  const completeOnboarding = () => {
    setIsOnboardingActive(false);
    setCurrentStep(totalSteps);
    OnboardingManager.markCompleted(user?.id);
  };

  const resetOnboarding = () => {
    setCurrentStep(0);
    setIsOnboardingActive(false);
    OnboardingManager.reset(user?.id);
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step);
    }
  };

  const value: OnboardingContextType = {
    currentStep,
    totalSteps,
    isOnboardingActive,
    startOnboarding,
    skipOnboarding,
    completeOnboarding,
    resetOnboarding,
    nextStep,
    previousStep,
    goToStep,
    userRole,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
